import { czyWidziDostawy } from '@/lib/auth/dostepZrodel';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useServices } from '@/lib/ServicesContext';
import { useAuth } from '@/lib/auth/AuthContext';
import { createAnalyticsApi, type OpinieZapytanie, type ParytetZapytanie, type SalesQuery, type SchoolSalesRow, type TransactionQuery, type ZapytanieTransakcjiRestauracji, type ZapytanieRaportuDostaw } from '@/lib/api/analytics';
import { createDriveApi, type DeliveryQuery } from '@/lib/api/drive';
import { assessJoin, findCoincidences, type SalesWindowRow } from '@/lib/crossSystem';
import { useDateRange, type DateRange } from '@/lib/useDateRange';

export function useAnalyticsApi() {
  const { api } = useServices();
  return useMemo(() => createAnalyticsApi(api), [api]);
}

export function useDriveApi() {
  const { api } = useServices();
  return useMemo(() => createDriveApi(api), [api]);
}

/** Wspólne ustawienia: nie ponawiamy błędów autoryzacji ani braku endpointu. */
const QUERY_OPTIONS = {
  staleTime: 60_000,
  retry: (failureCount: number, error: unknown) => {
    const kind = (error as { kind?: string })?.kind;
    if (kind === 'unauthorized' || kind === 'forbidden' || kind === 'not-found') return false;
    return failureCount < 2;
  },
} as const;

// ── Sprzedaż (CRM) ────────────────────────────────────────────────────────────

export function useSchoolSales(query: SalesQuery = {}) {
  const api = useAnalyticsApi();
  const [range] = useDateRange();
  const params = { ...range, ...query };
  return useQuery({ queryKey: ['school-sales', params], queryFn: () => api.schoolSales(params), ...QUERY_OPTIONS });
}

export function useRestaurantSales(query: SalesQuery = {}) {
  const api = useAnalyticsApi();
  const [range] = useDateRange();
  const params = { ...range, ...query };
  return useQuery({ queryKey: ['restaurant-sales', params], queryFn: () => api.restaurantSales(params), ...QUERY_OPTIONS });
}

export function useDishSales(query: SalesQuery = {}) {
  const api = useAnalyticsApi();
  const [range] = useDateRange();
  const params = { ...range, ...query };
  return useQuery({ queryKey: ['dish-sales', params], queryFn: () => api.dishSales(params), ...QUERY_OPTIONS });
}

// ── Dostawy (Drive) ───────────────────────────────────────────────────────────
// Zapytania Drive są WŁĄCZANE dopiero po podłączeniu sesji Drive. Bez tego lecielibyśmy
// w 401 przy każdym renderze i zaśmiecali ekran błędem, który nie jest błędem.

function useDriveEnabled() {
  const auth = useAuth();
  return czyWidziDostawy(auth);
}

export function useDeliverySummary(query: DeliveryQuery = {}) {
  const api = useDriveApi();
  const [range] = useDateRange();
  const enabled = useDriveEnabled();
  const params = { ...range, ...query };
  return useQuery({ queryKey: ['delivery-summary', params], queryFn: () => api.summary(params), enabled, ...QUERY_OPTIONS });
}

export function useDeliveryBySchool(query: DeliveryQuery = {}) {
  const api = useDriveApi();
  const [range] = useDateRange();
  const enabled = useDriveEnabled();
  const params = { ...range, ...query };
  return useQuery({ queryKey: ['delivery-by-school', params], queryFn: () => api.bySchool(params), enabled, ...QUERY_OPTIONS });
}

export function useDeliveryTrend(query: DeliveryQuery = {}) {
  const api = useDriveApi();
  const [range] = useDateRange();
  const enabled = useDriveEnabled();
  const params = { granularity: 'day' as const, ...range, ...query };
  return useQuery({ queryKey: ['delivery-trend', params], queryFn: () => api.trend(params), enabled, ...QUERY_OPTIONS });
}

export function useDeliveryHistory(query: DeliveryQuery = {}) {
  const api = useDriveApi();
  const [range] = useDateRange();
  const enabled = useDriveEnabled();
  const params = { limit: 100, ...range, ...query };
  return useQuery({ queryKey: ['delivery-history', params], queryFn: () => api.history(params), enabled, ...QUERY_OPTIONS });
}

export function useTuszamaIdCoverage(query: DeliveryQuery = {}) {
  const api = useDriveApi();
  const [range] = useDateRange();
  const enabled = useDriveEnabled();
  const params = { ...range, ...query };
  return useQuery({ queryKey: ['tuszama-coverage', params], queryFn: () => api.coverage(params), enabled, ...QUERY_OPTIONS });
}

// ── Analiza łączona ───────────────────────────────────────────────────────────

/** Poprzednie okno o TEJ SAMEJ długości — ta sama reguła co po stronie backendów. */
function previousWindow(range: DateRange): DateRange {
  const from = new Date(`${range.date_from}T00:00:00Z`);
  const to = new Date(`${range.date_to}T00:00:00Z`);
  const days = Math.floor((to.getTime() - from.getTime()) / 86400000) + 1;
  const prevTo = new Date(from.getTime() - 86400000);
  const prevFrom = new Date(prevTo.getTime() - (days - 1) * 86400000);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { date_from: iso(prevFrom), date_to: iso(prevTo) };
}

function toSalesWindow(rows: readonly SchoolSalesRow[]): SalesWindowRow[] {
  return rows.map((r) => ({
    school_id: r.school_id,
    school_name: r.school_name,
    city: r.city,
    sales_grosze: r.period_sales_grosze,
    orders: r.period_orders,
  }));
}

/**
 * Zbieżność sprzedaży i dostaw. Wymaga OBU sesji — bez podłączonego Drive nie ma z czym łączyć.
 * Zwraca też raport złączenia, żeby dało się odróżnić „brak sygnału” od „nie da się policzyć”.
 */
export function useSalesDeliveryCoincidence() {
  const api = useAnalyticsApi();
  const [range] = useDateRange();
  const previous = useMemo(() => previousWindow(range), [range]);
  const driveConnected = useDriveEnabled();

  const current = useSchoolSales();
  const bySchool = useDeliveryBySchool();
  const prior = useQuery({
    queryKey: ['school-sales', previous],
    queryFn: () => api.schoolSales(previous),
    enabled: driveConnected,
    ...QUERY_OPTIONS,
  });

  const result = useMemo(() => {
    if (!driveConnected) {
      // Po przejściu na jedno logowanie to NIE jest już „brak sesji Drive" — sesja jest jedna.
      // To brak UPRAWNIENIA do danych o dostawach, a bez nich nie ma czego z czym zestawiać.
      return { available: false as const, reason: 'Brak uprawnienia do danych o dostawach.' };
    }
    if (!current.data || !prior.data || !bySchool.data) return null;
    const salesCurrent = toSalesWindow(current.data.schools);
    const salesPrevious = toSalesWindow(prior.data.schools);
    const zbieznosci = findCoincidences(salesCurrent, salesPrevious, bySchool.data.schools);
    return {
      available: true as const,
      join: assessJoin(salesCurrent, bySchool.data.schools),
      coincidences: zbieznosci.coincidences,
      // POPULACJA jedzie razem z wynikiem, bo bez niej pusta lista jest nie do zinterpretowania:
      // „przeanalizowano i nic nie ma" i „nie było czego przeanalizować" wyglądają identycznie.
      population: zbieznosci.population,
      window: range,
      comparison: previous,
    };
  }, [driveConnected, current.data, prior.data, bySchool.data, range, previous]);

  return {
    data: result,
    isLoading: driveConnected && (current.isLoading || prior.isLoading || bySchool.isLoading),
    error: current.error ?? prior.error ?? bySchool.error,
  };
}

// ── O1: rejestr płatności i katalog dań ──────────────────────────────────────

export function useTransactions(query: TransactionQuery = {}) {
  const api = useAnalyticsApi();
  return useQuery({ queryKey: ['transactions', query], queryFn: () => api.transactions(query), ...QUERY_OPTIONS });
}

export function useTransactionDetail(id: number | null) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['transaction', id],
    queryFn: () => api.transactionDetail(id as number),
    // Bez identyfikatora nie ma czego pobierać — zapytanie wyłączone, a nie wołane z null-em.
    enabled: id !== null,
    ...QUERY_OPTIONS,
  });
}

export function useDishCatalog(query: { restaurant_id?: number; search?: string } = {}) {
  const api = useAnalyticsApi();
  return useQuery({ queryKey: ['dish-catalog', query], queryFn: () => api.dishCatalog(query), ...QUERY_OPTIONS });
}

/**
 * Słowniki referencyjne. Świadomie osobne hooki od `useSchoolSales` / `useRestaurantSales`:
 * tamte pytają „gdzie coś kupiono", te — „co w ogóle istnieje". Wspólny hook zachęcałby do
 * mieszania świeżości jednego ze świeżością drugiego, a to jest dokładnie ten błąd, którego
 * ten ekran ma nie popełniać.
 */
export function useSchoolsDictionary() {
  const api = useAnalyticsApi();
  return useQuery({ queryKey: ['schools-dictionary'], queryFn: () => api.schoolsDictionary(), ...QUERY_OPTIONS });
}

export function useRestaurantsDictionary() {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['restaurants-dictionary'],
    queryFn: () => api.restaurantsDictionary(),
    ...QUERY_OPTIONS,
  });
}

// ── PARYTET ZE STARYM PANELEM: ZIARNO POZYCJI ZAMÓWIENIA ─────────────────────

export function useTransakcjeParytet(q: ParytetZapytanie = {}) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['transakcje-parytet', q],
    queryFn: () => api.transakcjeParytet(q),
    ...QUERY_OPTIONS,
  });
}

/** Listy wyboru filtrów — te same etykiety, którymi filtruje stary panel. */
export function useTransakcjeParytetSlowniki() {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['transakcje-parytet-slowniki'],
    queryFn: () => api.transakcjeParytetSlowniki(),
    ...QUERY_OPTIONS,
  });
}

// ── ADMINISTRACJA → GRAFIK ───────────────────────────────────────────────────

export function useGrafikRestauracje() {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['grafik-restauracje'],
    queryFn: () => api.grafikRestauracje(),
    ...QUERY_OPTIONS,
  });
}

export function useGrafik(restauracja: number | null) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['grafik', restauracja],
    queryFn: () => api.grafik(restauracja as number),
    // Bez wybranej restauracji nie ma czego pobierać — zapytanie wyłączone, a nie wołane z null-em.
    enabled: restauracja !== null,
    ...QUERY_OPTIONS,
  });
}

// ── ADMINISTRACJA → ORGANIZACJA I MENU ───────────────────────────────────────

export function useOrganizacja(restauracja?: number) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['organizacja', restauracja ?? null],
    queryFn: () => api.organizacja(restauracja),
    ...QUERY_OPTIONS,
  });
}

export function useMenuDoModeracji(stan = 'oczekujace') {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['menu-moderacja', stan],
    queryFn: () => api.menuDoModeracji(stan),
    ...QUERY_OPTIONS,
  });
}

export function useOpinie(q: OpinieZapytanie = {}) {
  const api = useAnalyticsApi();
  return useQuery({ queryKey: ['opinie', q], queryFn: () => api.opinie(q), ...QUERY_OPTIONS });
}

/** Kolejka wyjątków moderacji — domyślnie pokazuje tylko to, co wymaga uwagi. */
export function useKolejkaModeracji() {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['kolejka-moderacji'],
    queryFn: () => api.kolejkaModeracji(),
    ...QUERY_OPTIONS,
  });
}

// ── PANEL RESTAURACJI ────────────────────────────────────────────────────────

export function useDzisiejszeZamowienia(restauracja?: number, dzien?: string) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['restauracja-dzisiaj', restauracja ?? null, dzien ?? null],
    queryFn: () => api.dzisiejszeZamowienia(restauracja, dzien),
    /*
     * Ekran jest używany W TRAKCIE przygotowania posiłków. Dane, które cicho się starzeją,
     * są tu gorsze niż brak danych — kucharz ugotuje wtedy według stanu sprzed godziny.
     * Odświeżamy więc co minutę i przy powrocie do okna.
     */
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    ...QUERY_OPTIONS,
  });
}

export function useSzkolyRestauracji(restauracja?: number) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['restauracja-szkoly', restauracja ?? null],
    queryFn: () => api.szkolyRestauracji(restauracja),
    ...QUERY_OPTIONS,
  });
}

/**
 * ZDOLNOŚCI ŚRODOWISKA — co naprawdę działa w tym wdrożeniu.
 *
 * Trzymane dłużej niż zwykłe dane: to nie są liczby, które zmieniają się w trakcie pracy,
 * tylko kształt środowiska. Odpytywanie o to przy każdym ekranie byłoby hałasem.
 */
export function useZdolnosci() {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['zdolnosci'],
    queryFn: () => api.zdolnosci(),
    ...QUERY_OPTIONS,
    // PO rozwinięciu wspólnych opcji — inaczej `QUERY_OPTIONS` nadpisałoby tę wartość
    // i zdolności odpytywane byłyby tak często jak zwykłe dane. To nie są liczby zmieniające
    // się w trakcie pracy, tylko kształt środowiska.
    staleTime: 5 * 60_000,
  });
}

export function useDaniaRestauracji(restauracja?: number) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: ['restauracja-dania', restauracja ?? null],
    queryFn: () => api.daniaRestauracji(restauracja),
    ...QUERY_OPTIONS,
  });
}

/**
 * Historia transakcji restauracji. Stronicowanie po stronie serwera — `keepPreviousData` sprawia,
 * że przy przejściu na kolejną stronę tabela nie miga pustką, tylko przyciemnia poprzednie wiersze.
 */
export function useTransakcjeRestauracji(zapytanie: ZapytanieTransakcjiRestauracji = {}) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: [
      'restauracja-transakcje',
      zapytanie.restauracja ?? null,
      zapytanie.od ?? null,
      zapytanie.do ?? null,
      zapytanie.strona ?? 1,
    ],
    queryFn: () => api.transakcjeRestauracji(zapytanie),
    placeholderData: (poprzednie) => poprzednie,
    ...QUERY_OPTIONS,
  });
}

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  RAPORTY DOSTAW — jedno zapytanie na sekcję, wspólny klucz okresu
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Klucz zapytania zawiera KOMPLET filtrów. Pominięcie któregokolwiek dawałoby wynik z pamięci
 * podręcznej dla innego zestawu — czyli tabelę spóźnień jednego kierowcy podpisaną nazwiskiem
 * drugiego. W raporcie, na którym opiera się rozmowa z kierowcą, jest to najgorszy możliwy błąd.
 */
function kluczRaportu(nazwa: string, z: ZapytanieRaportuDostaw) {
  return [
    nazwa, z.restauracja ?? null, z.od ?? null, z.do ?? null,
    z.kierowca ?? null, z.szkola ?? null, z.status ?? null,
    z.punktualnosc ?? null, z.zgloszenie ?? null, z.trafnosc ?? null,
    z.strona ?? 1, z.rozmiar ?? null,
  ];
}

export function usePodsumowanieDostaw(z: ZapytanieRaportuDostaw = {}) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: kluczRaportu('dostawy-podsumowanie', z),
    queryFn: () => api.raportDostawPodsumowanie(z),
    ...QUERY_OPTIONS,
  });
}

export function useKierowcyDostaw(z: ZapytanieRaportuDostaw = {}) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: kluczRaportu('dostawy-kierowcy', z),
    queryFn: () => api.raportDostawKierowcy(z),
    ...QUERY_OPTIONS,
  });
}

export function useSzkolyDostaw(z: ZapytanieRaportuDostaw = {}) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: kluczRaportu('dostawy-szkoly', z),
    queryFn: () => api.raportDostawSzkoly(z),
    ...QUERY_OPTIONS,
  });
}

export function useDniDostaw(z: ZapytanieRaportuDostaw = {}) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: kluczRaportu('dostawy-dni', z),
    queryFn: () => api.raportDostawDni(z),
    ...QUERY_OPTIONS,
  });
}

export function useListaDostaw(z: ZapytanieRaportuDostaw = {}) {
  const api = useAnalyticsApi();
  return useQuery({
    queryKey: kluczRaportu('dostawy-lista', z),
    queryFn: () => api.raportDostawLista(z),
    placeholderData: (poprzednie) => poprzednie,
    ...QUERY_OPTIONS,
  });
}

export function useFiltryDostaw(z: ZapytanieRaportuDostaw = {}) {
  const api = useAnalyticsApi();
  return useQuery({
    // Opcje filtrów zależą WYŁĄCZNIE od okresu i restauracji — nie od wybranych filtrów.
    // Inaczej wybranie kierowcy skasowałoby z listy pozostałych i nie dałoby się go zmienić.
    queryKey: ['dostawy-filtry', z.restauracja ?? null, z.od ?? null, z.do ?? null],
    queryFn: () => api.raportDostawFiltry({ restauracja: z.restauracja, od: z.od, do: z.do }),
    ...QUERY_OPTIONS,
  });
}
